import { ProjectLicenseDialog } from "../modelDialogs/ProjectLicenseDialog";
import { EstadaRadioGroup } from "../components/EstadaRadioGroup";
import "../App.scss";
import { ChevronDown, GripVertical, Settings } from "lucide-react";
import { CompanyBrandSettings } from "../settingPages/CompanyBrandSettings";
import { PrefixMode } from "../models/SettingsEnums";
import { useEffect, useState, type ReactNode } from "react";
import { C1_Dialog } from "../../ConceptOne/src/components/C1_Dialog";
import { C1_RoombookViewModeSelect } from "../../ConceptOne/src/raumbuch/C1_RoombookViewModeSelect";
import {
  defaultPdfChapters,
  excelChapter,
  isExcelChapter,
  isPdfChapterAvailable,
  availablePdfConfig,
  pdfChapterTitle,
  type PdfApplicationMode,
  type PdfCabinetMode,
  type PdfChapterConfig,
  type PdfChapterId,
  type PdfExportHandler,
} from "./PdfExportConfig";
import { readPdfExportConfig } from "./PdfExportSettings";
import { isUiModeDeveloper } from "../uiMode";
import { getPdfDevelopmentLicensed, setPdfDevelopmentLicensed, readPdfLicensePolicy } from "./PdfLicensePolicy";
import "../../ConceptOne/src/C1_styles.scss";
import "../../ConceptOne/src/raumbuch/C1_Raumbuch.scss";

/** Shares column alignment and spacing across all document properties. */
function SettingRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="C1_masterPdfSettingRow">
      <span>{label}</span>
      <div>{children}</div>
    </div>
  );
}

/** Displays the properties supported by a chapter without changing other chapters. */
function ChapterSettings({
  chapter,
  update,
  format = "pdf",
}: {
  chapter: PdfChapterConfig;
  update: (patch: Partial<PdfChapterConfig>) => void;
  format?: "pdf" | "excel";
}) {
  if (!chapter.enabled) return null;
  const rows: ReactNode[] = [];
  if (chapter.id === "actorList" && format === "pdf") {
    rows.push(<SettingRow key="actorImage" label="Produktbild anzeigen">
      <input type="checkbox" aria-label="Produktbild anzeigen" checked={chapter.actorShowImage ?? true}
        onChange={event => update({ actorShowImage: event.currentTarget.checked })} />
    </SettingRow>);
  }
  if (chapter.id === "consumerConnections") {
    rows.push(<SettingRow key="consumerRooms" label="Nach Räumen gegliedert">
      <input type="checkbox" aria-label="Nach Räumen gegliedert" checked={chapter.consumerGroupByRoom ?? true}
        onChange={event => update({ consumerGroupByRoom: event.currentTarget.checked })} />
    </SettingRow>);
  }
  if (chapter.id === "cablePull") {
    rows.push(<SettingRow key="cableRoomPages" label="Eine Seite pro Raum">
      <input type="checkbox" aria-label="Eine Seite pro Raum" checked={chapter.cablePullPagePerRoom ?? false}
        onChange={event => update({ cablePullPagePerRoom: event.currentTarget.checked })} />
    </SettingRow>);
  }
  if (chapter.id === "terminals") {
    if (format === "pdf") rows.push(<SettingRow key="terminalMode" label="Darstellung">
      <EstadaRadioGroup className="C1_roombookViewMode" label="Darstellung der Klemmen"
        value={chapter.terminalMode ?? "plan"} onChange={terminalMode => update({ terminalMode })}
        options={[{ value: "list", label: "Liste" }, { value: "plan", label: "Plan" }]} />
    </SettingRow>);
    rows.push(<SettingRow key="unusedWires" label="Unbenutzte Adern immer darstellen">
      <input type="checkbox" aria-label="Unbenutzte Adern immer darstellen"
        checked={chapter.terminalShowUnusedWires ?? false}
        onChange={event => update({ terminalShowUnusedWires: event.currentTarget.checked })} />
    </SettingRow>);
    if (format === "pdf") rows.push(<SettingRow key="terminalSpacing" label="Zeilenabstand">
      <select aria-label="Zeilenabstand der Klemmen" value={chapter.terminalSpacing ?? "normal"}
        onChange={event => update({ terminalSpacing: event.currentTarget.value === "wide" ? "wide" : event.currentTarget.value === "normal" ? "normal" : "compact" })}>
        <option value="compact">Kompakt</option><option value="normal">Normal</option><option value="wide">Groß</option>
      </select>
    </SettingRow>);
    if (chapter.terminalMode === "list" && format === "pdf") rows.push(
      <SettingRow key="viaExplanation" label="Erklärung Verkabelung">
        <input type="checkbox" aria-label="Erklärung Verkabelung"
          checked={chapter.terminalViaExplanation ?? false}
          onChange={event => update({ terminalViaExplanation: event.currentTarget.checked })} />
      </SettingRow>);
  }
  if (chapter.id === "cablePull" || chapter.id === "productPrices") {
    const cables = chapter.id === "cablePull";
    const label = cables ? "Kabelanzahl anzeigen" : "Ein Schaltschrank pro Seite";
    rows.push(<SettingRow key="technicalDetails" label={label}>
      <input type="checkbox" aria-label={label} checked={cables ? chapter.cablePullCounts ?? true : chapter.pricesPerCabinet ?? false}
        onChange={event => update(cables ? { cablePullCounts: event.currentTarget.checked } : { pricesPerCabinet: event.currentTarget.checked })} />
    </SettingRow>);
  }
  if (chapter.id === "cabinetLayout") {
    rows.push(
      <SettingRow key="cabinetMode" label="Schaltschrank">
        <select aria-label="Schaltschrankdarstellung" value={chapter.cabinetMode ?? "schematic"}
          onChange={event => update({ cabinetMode: event.currentTarget.value as PdfCabinetMode })}>
          <option value="schematic">Schematisch</option><option value="graphic">Grafisch</option>
          <option value="v1">V1</option><option value="v2">V2</option>
        </select>
      </SettingRow>,
      <SettingRow key="cabinetRailMode" label="Einzelne Schienen">
        <select aria-label="Schienendarstellung" value={chapter.cabinetRailMode ?? "off"}
          onChange={event => update({ cabinetRailMode: event.currentTarget.value as PdfCabinetMode | "off" })}>
          <option value="off">Aus</option><option value="schematic">Schematisch</option>
          <option value="graphic">Grafisch</option><option value="v1">V1</option><option value="v2">V2</option>
        </select>
      </SettingRow>,
    );
  }
  if (chapter.id === "fuseList") {
    if (format === "pdf") rows.push(
      <SettingRow key="fuseMode" label="Darstellung">
        <EstadaRadioGroup className="C1_roombookViewMode" label="Sicherungsdarstellung"
          value={chapter.fuseMode ?? "list"} onChange={fuseMode => update({ fuseMode })}
          options={[{ value: "plan", label: "Plan" }, { value: "list", label: "Liste" }]} />
      </SettingRow>);
    rows.push(
      <SettingRow key="fuseSort" label="Sortierung">
        <EstadaRadioGroup className="C1_roombookViewMode" label="Sortierung unterhalb der Sicherung"
          value={chapter.fuseSort ?? "consumer"} onChange={fuseSort => update({ fuseSort })}
          options={[{ value: "actorChannel", label: "AktorKanal" }, { value: "terminal", label: "Klemme" },
            { value: "consumer", label: "Verbraucher" }]} />
      </SettingRow>,
    );
  }
  if (chapter.id === "groupAddresses") {
    rows.push(
      <SettingRow key="detailedUsage" label="Detaillierter Verwendungsnachweis">
        <input
          type="checkbox"
          aria-label="Detaillierter Verwendungsnachweis"
          checked={chapter.detailedGroupAddressUsage ?? false}
          onChange={(event) => update({ detailedGroupAddressUsage: event.currentTarget.checked })}
        />
      </SettingRow>,
    );
  }
  if (chapter.id === "floorPlans" || chapter.id === "roomPlans") {
    rows.push(
      <SettingRow key="mode" label="Darstellung">
        <EstadaRadioGroup className="C1_roombookViewMode" label="Plan-Darstellung"
          value={chapter.planMode ?? "design"} onChange={planMode => update({ planMode })}
          options={[{ value: "design", label: "Design" }, { value: "technical", label: "Technik" },
            ...(globalThis.state.projectMetadata.isC1Project ? [{ value: "din" as const, label: "DIN" }] : [])]} />
      </SettingRow>,
    );
  }
  if ((chapter.id === "floorPlans" || chapter.id === "roomPlans") && globalThis.state.projectMetadata.isC1Project) {
    rows.push(
      <SettingRow key="symbolZoom" label="Symbol-Zoom">
        <input type="number" aria-label="Symbol-Zoom" min="0.5" max="2" step="0.1"
          value={chapter.eloItemScale ?? 1}
          onChange={event => {
            const value = event.currentTarget.valueAsNumber;
            if (Number.isFinite(value)) update({ eloItemScale: Math.min(2, Math.max(0.5, value)) });
          }} />
      </SettingRow>,
    );
  }
  if (chapter.id === "floorPlans" && globalThis.state.projectMetadata.isC1Project) {
    rows.push(
      <SettingRow key="dimensions" label="Maße anzeigen">
        <input type="checkbox" aria-label="Maße anzeigen"
          checked={chapter.includeDimensions ?? true}
          onChange={event => update({ includeDimensions: event.currentTarget.checked })} />
      </SettingRow>,
    );
  }
  if (chapter.id === "roomPlans") {
    rows.push(
      <SettingRow key="tags" label="Tags darstellen">
        <input
          type="checkbox"
          aria-label="Tags darstellen"
          checked={chapter.showTags ?? true}
          onChange={(event) => update({ showTags: event.currentTarget.checked })}
        />
      </SettingRow>,
    );
  }
  if (chapter.id === "roombook" && format === "pdf") {
    rows.push(
      <SettingRow key="grouping" label="Gliederung">
        {globalThis.state.projectMetadata.isC1Project ? <C1_RoombookViewModeSelect
          value={chapter.roombookMode ?? "functions"}
          onChange={(roombookMode) => update({ roombookMode })}
        /> : <span>Funktionen</span>}
      </SettingRow>,
    );
  }
  if (chapter.id === "billOfMaterials") {
    rows.push(
      <SettingRow key="without-products" label="Ohne Produktzuordnung">
        <input type="checkbox" aria-label="Geräte ohne Produktzuordnung ausgeben"
          checked={chapter.billOfMaterialsWithoutProducts ?? true}
          onChange={event => update({ billOfMaterialsWithoutProducts: event.currentTarget.checked })} />
      </SettingRow>,
    );
    rows.push(
      <SettingRow key="grouping" label="Gliederung">
        <EstadaRadioGroup className="C1_roombookViewMode" label="Stücklisten-Gliederung"
          value={chapter.billOfMaterialsMode ?? "total"} onChange={billOfMaterialsMode => update({ billOfMaterialsMode })}
          options={[{ value: "total", label: "Gesamt" }, { value: "floors", label: "Etagenweise" }]} />
      </SettingRow>,
    );
    rows.push(
      <SettingRow key="usage" label="Verwendung">
        <input
          type="checkbox"
          aria-label="Verwendungsnachweis ausgeben"
          checked={chapter.billOfMaterialsUsage ?? false}
          onChange={(event) => update({ billOfMaterialsUsage: event.currentTarget.checked })}
        />
      </SettingRow>,
    );
    rows.push(
      <SettingRow key="lights" label="Leuchten-Bauart">
        <input
          type="checkbox"
          aria-label="Leuchten nach Bauart unterscheiden"
          checked={chapter.billOfMaterialsLightTypes ?? false}
          onChange={(event) => update({ billOfMaterialsLightTypes: event.currentTarget.checked })}
        />
      </SettingRow>,
    );
  }
  return rows.length ? <div className="C1_masterPdfChapterSettings">{rows}</div> : null;
}

/** Selects document chapters using the current application's independent project settings. */
export function PdfExportSelectionDialog({
  mode,
  open,
  onClose,
  onExport,
  format = "pdf",
}: {
  mode: PdfApplicationMode;
  open: boolean;
  onClose: () => void;
  onExport: PdfExportHandler;
  format?: "pdf" | "excel";
}) {
  const [chapters, setChapters] = useState(() => defaultPdfChapters(mode));
  const [draggedId, setDraggedId] = useState<PdfChapterId>();
  const [licensed, setLicensed] = useState(getPdfDevelopmentLicensed);
  const [showSettings, setShowSettings] = useState(false);
  const [separateTagAndName, setSeparateTagAndName] = useState(true);
  const [useTags, setUseTags] = useState(true);
  const [useDinPrefixes, setUseDinPrefixes] = useState(() => (globalThis.state?.settings?.prefixMode ?? PrefixMode.DIN) === PrefixMode.DIN);
  const developer = isUiModeDeveloper();
  useEffect(() => {
    if (open) {
      const config = readPdfExportConfig(globalThis.state, mode);
      setChapters(format === "excel" ? config.chapters.filter(isExcelChapter).map(excelChapter) : config.chapters);
      setSeparateTagAndName(config.separateTagAndName ?? true);
      setUseTags(config.useTags ?? true);
      setUseDinPrefixes((globalThis.state?.settings?.prefixMode ?? PrefixMode.DIN) === PrefixMode.DIN);
      setLicensed(getPdfDevelopmentLicensed());
      setShowSettings(false);
    }
    setDraggedId(undefined);
  }, [open, mode, format]);

  const updateChapter = (id: PdfChapterId, patch: Partial<PdfChapterConfig>) => {
    setChapters((current) => current.map((chapter) => (chapter.id === id ? { ...chapter, ...patch } : chapter)));
  };
  const moveChapter = (targetId: PdfChapterId) => {
    if (!draggedId || draggedId === targetId) return;
    setChapters((current) => {
      const next = [...current];
      const from = next.findIndex((chapter) => chapter.id === draggedId);
      const to = next.findIndex((chapter) => chapter.id === targetId);
      if (from < 0 || to < 0) return current;
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };
  const exportPdf = (singleChapterId?: PdfChapterId) => {
    const config = availablePdfConfig({ chapters, useTags, separateTagAndName }, globalThis.state);
    if (singleChapterId && !isPdfChapterAvailable({ id: singleChapterId }, globalThis.state)) return;
    if (!singleChapterId && !config.chapters.some((chapter) => chapter.enabled)) return;
    if (developer) setPdfDevelopmentLicensed(licensed);
    onClose();
    window.setTimeout(() => void onExport(config, mode, singleChapterId), 0);
  };
  let chapterNumber = 0;
  const availableChapters = chapters.filter(chapter => isPdfChapterAvailable(chapter, globalThis.state));
  const allEnabled = availableChapters.length > 0 && availableChapters.every(chapter => chapter.enabled);
  const chapterViews = chapters.map((chapter) => {
    const available = isPdfChapterAvailable(chapter, globalThis.state);
    chapter = { ...chapter, enabled: chapter.enabled && available };
    if (chapter.enabled) chapterNumber += 1;
    return (
      <section
        key={chapter.id}
        className={`C1_masterPdfChapter${chapter.enabled ? "" : " C1_masterPdfChapter--disabled"}`}
        onDragOver={(event) => event.preventDefault()}
        onDrop={() => moveChapter(chapter.id)}
      >
        <div
          className="C1_masterPdfDragHandle"
          draggable
          title="Kapitel verschieben"
          onDragStart={() => setDraggedId(chapter.id)}
          onDragEnd={() => setDraggedId(undefined)}
        >
          <GripVertical />
        </div>
        <div className="C1_masterPdfChapterContent">
          <div className="C1_masterPdfChapterTopRow">
          <label className="C1_masterPdfChapterHeader">
            <input
              type="checkbox"
              disabled={!available}
              checked={chapter.enabled}
              onChange={(event) => updateChapter(chapter.id, { enabled: event.currentTarget.checked })}
            />
            <span>
              <strong>
                {chapter.enabled ? `Kapitel ${chapterNumber} · ` : ""}
                {pdfChapterTitle(chapter)}
              </strong>
            </span>
          </label>
          <button type="button" className="button white C1_masterPdfSingleExport" onClick={() => exportPdf(chapter.id)}
            disabled={!available} title={available ? undefined : "Keine Szenen im Projekt vorhanden"}
            aria-label={`${pdfChapterTitle(chapter)} einzeln ausgeben`}>Einzeln ausgeben</button>
          </div>
          <ChapterSettings chapter={chapter} format={format} update={(patch) => updateChapter(chapter.id, patch)} />
        </div>
      </section>
    );
  });
  return (
    <C1_Dialog
      open={open}
      title={format === "excel" ? "Excel-Dokumentation erzeugen" : "PDF erzeugen"}
      headerActions={
        <button type="button" className="C1_masterPdfSettingsToggle" aria-label="Einstellungen"
          title="Einstellungen" aria-pressed={showSettings} aria-expanded={showSettings}
          onPointerDown={event => event.stopPropagation()}
          onClick={() => setShowSettings(current => !current)}>
          <Settings size={18} aria-hidden="true" />
          Einstellungen
          <ChevronDown size={16} aria-hidden="true" style={{ transform: showSettings ? "rotate(180deg)" : undefined }} />
        </button>
      }
      confirmLabel={format === "excel" ? "Excel erstellen" : "PDF erstellen"}
      showConfirm={!showSettings}
      className="C1_masterPdfDialog app-settings"
      onCancel={onClose}
      onConfirm={() => exportPdf()}
    >
      {developer && (
        <label className="C1_masterPdfIntro" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={licensed} onChange={event => setLicensed(event.currentTarget.checked)} />
          Lizenziert
        </label>
      )}
      {showSettings && <div className="C1_masterPdfGeneralSettings" aria-label="Allgemeine PDF-Einstellungen">
        <section className="settings-card">
          <h3 className="settings-card__header">Firma</h3>
          <div className="settings-card__body"><CompanyBrandSettings /></div>
        </section>
        <section className="settings-card">
        <h3 className="settings-card__header">Darstellung Tag / Name</h3>
        <div className="settings-card__body">
        <label className="C1_masterPdfChapterHeader">
          <input type="checkbox" checked={useTags}
            onChange={event => setUseTags(event.currentTarget.checked)} />
          Tags verwenden
        </label>
        {useTags && <>
        <label className="C1_masterPdfChapterHeader">
          <input type="checkbox" checked={separateTagAndName}
            onChange={event => setSeparateTagAndName(event.currentTarget.checked)} />
          Tag und Name durch Punkt trennen
        </label>
        <label className="C1_masterPdfChapterHeader">
          <input type="checkbox" checked={useDinPrefixes}
            onChange={event => {
              const enabled = event.currentTarget.checked;
              globalThis.state.settings.prefixMode = enabled ? PrefixMode.DIN : PrefixMode.DOT;
              setUseDinPrefixes(enabled);
            }} />
          DIN-Präfixe verwenden
        </label>
        </>}
        </div></section>
      </div>}
      {!showSettings && <p className="C1_masterPdfIntro">{format === "excel"
        ? "Listendokumente auswählen und ordnen. Mit Deckblatt, verlinktem Inhaltsverzeichnis und einem druckfertigen A4-Blatt je Dokument. Pläne werden als Listen ausgegeben."
        : "Kapitel aktivieren und per Ziehen in die gewünschte Reihenfolge bringen."}</p>}
      {!showSettings && <label className="C1_masterPdfChapterHeader C1_masterPdfSelectAll">
        <input type="checkbox" checked={allEnabled} onChange={event => {
          const enabled = event.currentTarget.checked;
          setChapters(current => current.map(chapter => ({...chapter, enabled: enabled && isPdfChapterAvailable(chapter, globalThis.state)})));
        }} />
        <strong>Alle</strong>
      </label>}
      {!showSettings && <div className="C1_masterPdfChapters">{chapterViews}</div>}
    </C1_Dialog>
  );
}

/** Shows the notice before either document selector, in C1 and the main app. */
export function PdfExportDialog(props: Parameters<typeof PdfExportSelectionDialog>[0]) {
  const [acceptedKey, setAcceptedKey] = useState<string>();
  const key = `${globalThis.state?.projectMetadata?.id}:${props.format ?? "pdf"}`;
  useEffect(() => { if (!props.open) setAcceptedKey(undefined); }, [props.open]);
  if (!props.open) return null;
  if (readPdfLicensePolicy(globalThis.state?.projectLicense).preview && acceptedKey !== key) {
    return <ProjectLicenseDialog key={key} context="documents"
      onCancel={props.onClose} onContinue={() => setAcceptedKey(key)} />;
  }
  return <PdfExportSelectionDialog {...props} />;
}
